import React, { useEffect, useState } from 'react';
import { PersonaInitialsColor, Text } from '@fluentui/react';
import './GridTable.scss';
import { Grid, GridColumn as Column, GridRowClickEvent, GridRowProps, GridCellProps } from '@progress/kendo-react-grid';
import { EllipseMenu } from '../triageMenu/EllipseMenu';
import { ColHeadMenu } from './headerCellMenus/HeaderCellMenus';
import useStore, { IStore } from 'app/store';
import { IColumnLookUpProps } from 'app/models/common/post';
import AgingProgress from 'app/components/agingProgress/AgingProgress';
import { Tooltip } from '@progress/kendo-react-tooltip';
import Avatar from 'app/components/avatar/Avatar';
import { StringConstants } from 'app/utils/constants';
import { RequestDto, RequestStatus } from 'generated/request-backend';
import { GetTypes } from 'app/components/helpers';
import { StateData } from '../manageRequests/ManageRequests';

interface IProps {
    requestData: RequestDto[];

    columnsLookUpData: IColumnLookUpProps[];
    page: number;
    setPage: (value: number) => void;

    isLoading: boolean;
    isPreviousData: boolean;

    isEditMode?: boolean;
    setIsEditMode?: (value: boolean) => void;
    isListView?: boolean;
    setIsListView?: (value: boolean) => void;
    setSelectedRequest?: (request: RequestDto) => void;
    gridContRef: React.RefObject<HTMLDivElement>;
    createdOnOrder: string;
    setCreatedOnOrder: (value: string) => void;
    agingOrder: string;
    setAgingOrder: (value: string) => void;
    revenueOrder: string;
    setRevenueOrder: (value: string) => void;
    setCurrentSortProp: (value: string) => void;
    stateData?: StateData;
}

interface GridItem {
    key: number;
    column: React.ReactElement;
}

const GridTable: React.FC<IProps> = ({
    requestData,

    columnsLookUpData,
    page,
    setPage,

    isEditMode,
    setIsEditMode,
    isListView,
    setIsListView,
    setSelectedRequest,
    gridContRef,
    createdOnOrder,
    setCreatedOnOrder,
    agingOrder,
    setAgingOrder,
    revenueOrder,
    setRevenueOrder,
    setCurrentSortProp,
    stateData
}) => {
    const [containerWidth, setContainerWidth] = useState(1367);

    const requestType = useStore((state: IStore) => state.requestType);
    const newlyAddedRow = useStore((state: IStore) => state.newlyAddedRow);

    const customCell = (props: GridCellProps) => {
        return (
            <td>
                {
                    <EllipseMenu
                        isHidden={requestType === StringConstants.HIDDEN_REQUEST}
                        row={props.dataItem}
                        origin={StringConstants.LIST_VIEW}
                        isEditMode={isEditMode}
                        setIsEditMode={setIsEditMode}
                        isListView={isListView}
                        setIsListView={setIsListView}
                        setSelectedRequest={setSelectedRequest}
                        rootIdName="grid-list-view-ellipse-id"
                        page={page}
                        setPage={setPage}
                    />
                }
            </td>
        );
    };
    const AssignedToCell = (props: { dataItem: RequestDto }) => {
        return (
            <td>
                {props.dataItem?.assignee ? (
                    <div className="assigned-to-cell">
                        <Avatar
                            userId={props.dataItem?.assignee?.id}
                            userName={props.dataItem?.assignee?.displayName}
                            initialColor={PersonaInitialsColor.coolGray}
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">{props.dataItem?.assignee?.displayName}</span>
                    </div>
                ) : (
                    <div className="assigned-to-cell">
                        <Avatar
                            userId={'0'}
                            userName={StringConstants.UNASSIGNED}
                            initialColor={PersonaInitialsColor.coolGray}
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">Unassigned</span>
                    </div>
                )}
            </td>
        );
    };

    const HeaderCell = () => {
        return <Text className="request-type-header">REQUEST TYPE</Text>;
    };
    const AgingHeader = () => {
        return (
            <ColHeadMenu
                type={StringConstants.UNASSIGNED}
                agingOrder={agingOrder}
                setAgingOrder={setAgingOrder}
                setCurrentSortProp={setCurrentSortProp}
            />
        );
    };
    const CreatedOnHeader = () => {
        return (
            <ColHeadMenu
                type={StringConstants.CREATED_ON}
                createdOnOrder={createdOnOrder}
                setCreatedOnOrder={setCreatedOnOrder}
                setCurrentSortProp={setCurrentSortProp}
                stateData={stateData}
            />
        );
    };
    const RevenueHeader = () => {
        return (
            <ColHeadMenu
                type={StringConstants.REVENUE}
                revenueOrder={revenueOrder}
                setRevenueOrder={setRevenueOrder}
                setCurrentSortProp={setCurrentSortProp}
                stateData={stateData}
            />
        );
    };

    const customAgingCell = (props: { dataItem: RequestDto }) => {
        return (
            <td>
                <AgingProgress
                    startDate={props.dataItem.createdOn}
                    endDate={props.dataItem.requestedResolutionDate}
                    closedDate={props.dataItem.closedOn}
                />
            </td>
        );
    };

    const TitleCell = (props: { dataItem: RequestDto }) => {
        return <td title={props.dataItem.summary}>{props.dataItem.summary}</td>;
    };

    const RequestCell = (props: { dataItem: RequestDto }) => {
        return <td title={props.dataItem.requestType.toString()}>{GetTypes(props.dataItem.requestType)}</td>;
    }

    const StatusCell = (props: { dataItem: RequestDto }) => {
        return <td title={props.dataItem.status.toString()}>{RequestStatus[props.dataItem.status]}</td>
    }

    const setWidth = () => {
        let columnCount = columnsLookUpData.filter((item) => item.isChecked).length;

        if (columnCount > 1) {
            let padding = 50 + 47 + 30;
            const availWidth = containerWidth - padding - 90;
            return parseInt((availWidth / columnCount).toString());
        } else {
            return containerWidth;
        }
    };
    const arr : GridItem[] = [
        {
            key: 0,
            column: <Column width={setWidth()} className="cell-in-bold" field="unifiedTrackingId" title="ID" resizable={false} />
        },
        {
            key: 1,
            column: <Column width={setWidth()} field="Title" title="TITLE" cell={TitleCell} resizable={false} />
        },
        {
            key: 2,
            column: (
                <Column width={setWidth()} title="REQUEST TYPE" cell={RequestCell} headerCell={HeaderCell} field="requestType" resizable={false} />
            )
        },
        {
            key: 3,
            column: <Column width={setWidth()} field="status" title="STATUS" cell={StatusCell} resizable={false} />
        },
        {
            key: 4,
            column: (
                <Column width={setWidth() + 30} field="assignedTo" title="ASSIGNED TO" cell={AssignedToCell} resizable={false} />
            )
        },
        {
            key: 5,
            column: (
                <Column width={setWidth()} field="createdOn" title="CREATED ON" headerCell={CreatedOnHeader} resizable={false} />
            )
        },
        {
            key: 6,
            column: <Column width={setWidth()} headerCell={AgingHeader} cell={customAgingCell} resizable={false} />
        },
        {
            key: 7,
            column: (
                <Column
                    width={setWidth() + 50}
                    field="totalRevenue"
                    title="TOTAL REVENUE RISK"
                    headerCell={RevenueHeader}
                    resizable={false}
                    className="right-aligned-column"
                />
            )
        },
        {
            key: 8,
            column: <Column width={90} reorderable={false} cell={customCell} resizable={false} />
        }
    ];

    const getColumns = () => {
        let list: GridItem[] = [];
        columnsLookUpData.forEach((item) => {
            arr.every((ele) => {
                if ((ele.key === item.key && item.isChecked) || item.key === 0) {
                    list.push(ele);
                    return false;
                } else {
                    return true;
                }
            });
        });
        list.push(arr[8]);
        return list;
    };
    const [columnList, setColumnList] = useState(getColumns());

    const onRowClick = (event: GridRowClickEvent) => {
        setIsEditMode(false);
        setIsListView(false);
        setSelectedRequest(event.dataItem);
    };

    const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
        const white = { backgroundColor: 'white' } as CSSStyleDeclaration;
        const blue = { backgroundColor: 'rgb(175,238,238)' } as CSSStyleDeclaration;
        const trProps: CSSStyleDeclaration = props.dataItem.id === newlyAddedRow ? blue : white;
        return React.cloneElement(trElement, { style: trProps }, trElement.props.children);
    };

    useEffect(() => {
        if (gridContRef.current?.offsetWidth) {
            setContainerWidth(gridContRef.current.offsetWidth);
        }
    }, [gridContRef.current]);

    useEffect(() => {
        setColumnList(getColumns());
        return () => {};
    }, [columnsLookUpData, requestType]);

    if (requestData) {
        return (
            <>
                <div className="grid-cont" role="main" aria-label="grid table">
                    <Tooltip openDelay={100} position="right" anchorElement="element">
                        <Grid data={requestData} sortable={true} rowRender={rowRender} onRowClick={onRowClick} resizable={false}>
                            {columnList?.map((item: GridItem) => item.column)}
                        </Grid>
                    </Tooltip>
                </div>
            </>
        );
    } else {
        <div>Loading...</div>;
    }
};

export default GridTable;
